import { produce } from 'immer';
import { isUndefined } from 'lodash/fp';
import { extractError } from 'app/lib/error';
import { UnionMode } from 'app/lib/constants';
import { reorderArray } from 'app/lib/utils';
import {
    INITIALIZE_CONFIG,
    ADD_MAPPED_DATASET,
    REMOVE_DATASET,
    SET_COLUMN_VALUE,
    ADD_NEW_OUTPUT_COLUMN,
    UPDATE_OUTPUT_COLUMN,
    SELECT_DATASET,
    CHANGE_UNION_MODE,
    CREATE_UNION_VIEW,
    UPDATE_UNION_VIEW,
    CLOSE_UNION_EDITOR,
    PREVIEW_UNION,
    REMOVE_OUTPUT_COLUMN,
    ADD_OUTPUT_COLUMNS,
    REORDER_OUTPUT_COLUMNS
} from './types.js';

export const INITIAL_STATE = {
    config: {
        inputDatasets: [],
        unionType: UnionMode.UNION_ALL,
        outputColumns: []
    },
    selectedDatasetIndex: 0,
    isSavingUnion: false,
    keepOpen: false,
    error: null
};

export const unionReducer = produce((draft, action) => {
    switch (action.type) {
        case INITIALIZE_CONFIG:
            draft.config = action.payload;
            draft.selectedDatasetIndex = 0;
            break;
        case ADD_MAPPED_DATASET:
            draft.config.inputDatasets.push(action.payload);
            break;
        case REMOVE_DATASET: {
            draft.config.inputDatasets.splice(action.payload.datasetIndex, 1);
            draft.selectedDatasetIndex = 0;
            break;
        }
        case SET_COLUMN_VALUE: {
            const { column, index, datasetIndex } = action.payload;
            const isNullValue =
                !isUndefined(column.value) &&
                (column.value === '' || column.value.toLowerCase() === 'null');
            draft.config.inputDatasets[datasetIndex].columns[index] = isNullValue
                ? { value: null }
                : column;
            break;
        }
        case ADD_NEW_OUTPUT_COLUMN: {
            const { column, defaultValue } = action.payload;
            const outputColumns = [
                ...draft.config.outputColumns,
                { name: column.name, type: column.type, defaultValue }
            ];
            const newInputColumn = { value: defaultValue };
            const inputDatasets = draft.config.inputDatasets.map((dataset) => {
                const columns = [...dataset.columns, newInputColumn];
                return { ...dataset, columns };
            });
            draft.config.outputColumns = outputColumns;
            draft.config.inputDatasets = inputDatasets;
            break;
        }
        case ADD_OUTPUT_COLUMNS: {
            const { outputColumns, mappedInputColumns } = action.payload;
            draft.config.outputColumns = [...draft.config.outputColumns, ...outputColumns];
            const inputDatasets = draft.config.inputDatasets.map((dataset) => {
                const columns = [...dataset.columns, ...mappedInputColumns[dataset.viewUid]];
                return { ...dataset, columns };
            });
            draft.config.inputDatasets = inputDatasets;
            break;
        }
        case UPDATE_OUTPUT_COLUMN: {
            const { columnName, index, type } = action.payload;
            draft.config.outputColumns[index] = {
                ...draft.config.outputColumns[index],
                name: columnName,
                type
            };
            break;
        }
        case REMOVE_OUTPUT_COLUMN: {
            const { index } = action.payload;
            draft.config.outputColumns.splice(index, 1);
            draft.config.inputDatasets.forEach((dataset) => {
                dataset.columns.splice(index, 1);
            });

            break;
        }
        case REORDER_OUTPUT_COLUMNS: {
            const { fromIndex, toIndex } = action.payload;
            draft.config.outputColumns = reorderArray(
                draft.config.outputColumns,
                fromIndex,
                toIndex
            );

            const inputDatasets = draft.config.inputDatasets.map((dataset) => {
                const columns = dataset.columns;
                return { ...dataset, columns: reorderArray(columns, fromIndex, toIndex) };
            });
            draft.config.inputDatasets = inputDatasets;

            break;
        }
        case PREVIEW_UNION: {
            draft.error = null;
            break;
        }
        case CREATE_UNION_VIEW.STARTED:
        case UPDATE_UNION_VIEW.STARTED:
            draft.isSavingUnion = true;
            draft.keepOpen = Boolean(action.meta?.keepOpen);
            break;
        case CREATE_UNION_VIEW.ENDED:
        case UPDATE_UNION_VIEW.ENDED:
            draft.isSavingUnion = false;
            break;
        case CREATE_UNION_VIEW.REJECTED:
        case CREATE_UNION_VIEW.FAILED:
        case UPDATE_UNION_VIEW.REJECTED:
        case UPDATE_UNION_VIEW.FAILED: {
            draft.error = extractError(action.payload);
            break;
        }
        case SELECT_DATASET: {
            draft.selectedDatasetIndex = action.payload.index;
            break;
        }
        case CHANGE_UNION_MODE:
            draft.config.unionType = action.payload.mode;
            break;
        case CLOSE_UNION_EDITOR:
            draft.selectedDatasetIndex = 0;
            draft.keepOpen = false;
            draft.config = INITIAL_STATE.config;
            break;
        default:
            break;
    }
}, INITIAL_STATE);
